import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import apiRequest from '../services/apiService';
import socketService from '../services/socketService';

const OrderNotificationContext = createContext();

// Fallback poll interval (only used if socket disconnects)
const FALLBACK_POLL_INTERVAL = 120000; // 2 minutes - increased to reduce server load
const DATA_CACHE_TIME = 30000; // 30 seconds - cache API responses to avoid duplicate calls

export const OrderNotificationProvider = ({ children }) => {
    const [notificationData, setNotificationData] = useState({
        totalActiveOrders: 0,
        unviewedCount: 0,
        receivedCount: 0,
        confirmedCount: 0,
        latestOrders: [],
        serverTime: null
    });
    const [isLoading, setIsLoading] = useState(false);
    const [lastViewedTimestamp, setLastViewedTimestamp] = useState(null);
    const [isWidgetOpen, setIsWidgetOpen] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [hasNewOrders, setHasNewOrders] = useState(false);
    const [selectedBranchId, setSelectedBranchId] = useState(null);
    const [isSocketConnected, setIsSocketConnected] = useState(false);
    
    const fallbackPollRef = useRef(null);
    const audioRef = useRef(null);
    const previousCountRef = useRef(0);
    const audioContextRef = useRef(null);
    const lastFetchRef = useRef(0);
    const lastDataRef = useRef(null);
    const isApiCallInProgressRef = useRef(false);
    const socketUnsubscribeRef = useRef(null);

    // Initialize notification sound with Web Audio API fallback
    useEffect(() => {
        // Try to use a notification sound file first
        audioRef.current = new Audio('/notification-sound.mp3');
        audioRef.current.volume = 0.5;
        
        // Initialize Web Audio API for fallback beep
        try {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        } catch (e) {
            console.log('Web Audio API not available');
        }
    }, []);

    // Play a simple beep using Web Audio API
    const playBeep = useCallback(() => {
        if (!audioContextRef.current) return;
        
        try {
            const context = audioContextRef.current;
            const oscillator = context.createOscillator();
            const gainNode = context.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(context.destination);
            
            oscillator.frequency.value = 800; // Frequency in Hz
            oscillator.type = 'sine';
            
            gainNode.gain.setValueAtTime(0.3, context.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.3);
            
            oscillator.start(context.currentTime);
            oscillator.stop(context.currentTime + 0.3);
        } catch (e) {
            console.log('Could not play beep:', e);
        }
    }, []);

    // Play notification sound
    const playNotificationSound = useCallback(() => {
        if (!soundEnabled) return;
        
        if (audioRef.current) {
            audioRef.current.play().catch(err => {
                console.log('Audio file not available, trying beep fallback');
                playBeep();
            });
        } else {
            playBeep();
        }
    }, [soundEnabled, playBeep]);

    // Fetch notification data from server with smart caching
    const fetchNotificationData = useCallback(async (silent = false, branchIdOverride = null, forceRefresh = false) => {
        const userData = JSON.parse(localStorage.getItem('userData'));
        if (!userData) return;

        // Check if API call is already in progress
        if (isApiCallInProgressRef.current) {
            console.log('[NotificationContext] API call already in progress, skipping...');
            return;
        }

        // Check cache validity (don't call API too frequently unless forced)
        const now = Date.now();
        if (!forceRefresh && (now - lastFetchRef.current) < DATA_CACHE_TIME && lastDataRef.current) {
            console.log('[NotificationContext] Using cached data, skipping API call');
            return lastDataRef.current;
        }

        if (!silent) setIsLoading(true);
        isApiCallInProgressRef.current = true;
        
        try {
            // Use override branch ID, or current selected branch, or default based on role
            let branchToFetch = branchIdOverride || selectedBranchId;
            if (!branchToFetch) {
                branchToFetch = userData.role === 1 ? userData.selectedBranch : userData._id;
            }
            
            console.log('[NotificationContext] Fetching notification data for branch:', branchToFetch);
            
            const response = await apiRequest('/orders/getNewOrdersNotification', 'POST', {
                lastViewedTimestamp,
                selectedBranchId: branchToFetch,
                clientCacheTimestamp: lastFetchRef.current
            });

            lastFetchRef.current = now;

            if (response.success && response.data) {
                const newData = response.data;
                
                // Check if data actually changed to avoid unnecessary updates
                const dataChanged = !lastDataRef.current || 
                    lastDataRef.current.totalActiveOrders !== newData.totalActiveOrders ||
                    lastDataRef.current.receivedCount !== newData.receivedCount ||
                    lastDataRef.current.confirmedCount !== newData.confirmedCount;
                
                if (dataChanged) {
                    console.log('[NotificationContext] Data changed, updating state');
                    setNotificationData(newData);
                    lastDataRef.current = newData;
                    
                    // Check if there are new orders since last fetch
                    if (newData.receivedCount > previousCountRef.current && previousCountRef.current !== 0) {
                        setHasNewOrders(true);
                        playNotificationSound();
                        
                        // Show browser notification if permitted
                        if (Notification.permission === 'granted') {
                            const order = newData.latestOrders[0];
                            if (order) {
                                new Notification('🛒 New Order Received!', {
                                    body: `${order.customerName} - ₹${order.totalAmount?.toLocaleString('en-IN') || 0}`,
                                    icon: '/zuntly_icon.png',
                                    tag: 'new-order',
                                requireInteraction: true
                                });
                            }
                        }
                    }
                    
                    previousCountRef.current = newData.receivedCount;
                } else {
                    console.log('[NotificationContext] No data changes detected');
                }
                
                return newData;
            }
        } catch (error) {
            console.error('[NotificationContext] Error fetching notification data:', error);
            // Don't update lastFetchRef on error to allow immediate retry
            lastFetchRef.current = 0;
        } finally {
            isApiCallInProgressRef.current = false;
            if (!silent) setIsLoading(false);
        }
    }, [lastViewedTimestamp, playNotificationSound, selectedBranchId]);

    // Handle socket notification events
    const handleSocketNotification = useCallback((data) => {
        console.log('[NotificationContext] Socket event received:', data);
        
        // Immediately fetch updated notification data
        fetchNotificationData(true);
        
        // Show visual/audio notification for new orders
        if (data.type === 'NEW_ORDER') {
            setHasNewOrders(true);
            playNotificationSound();
            
            // Show browser notification
            if (Notification.permission === 'granted') {
                new Notification('🛒 New Order Received!', {
                    body: `${data.customerName || 'Customer'} - ₹${data.totalAmount?.toLocaleString('en-IN') || 0}`,
                    icon: '/zuntly_icon.png',
                    tag: 'new-order',
                    requireInteraction: true
                });
            }
        }
    }, [fetchNotificationData, playNotificationSound]);

    // Mark orders as viewed
    const markOrdersAsViewed = useCallback(async () => {
        try {
            const now = new Date().toISOString();
            await apiRequest('/orders/markOrdersAsViewed', 'POST', {
                viewedTimestamp: now
            });
            setLastViewedTimestamp(now);
            setHasNewOrders(false);
            previousCountRef.current = 0;
            
            // Refresh notification data
            setTimeout(() => fetchNotificationData(true), 500);
        } catch (error) {
            console.error('Error marking orders as viewed:', error);
        }
    }, [fetchNotificationData]);

    // Get notification preferences on mount
    const getNotificationPreferences = useCallback(async () => {
        try {
            const response = await apiRequest('/orders/getNotificationPreferences', 'POST', {});
            if (response.success && response.data) {
                setLastViewedTimestamp(response.data.lastOrdersViewedAt);
            }
        } catch (error) {
            console.error('Error fetching notification preferences:', error);
        }
    }, []);

    // Request browser notification permission
    const requestNotificationPermission = useCallback(async () => {
        if ('Notification' in window && Notification.permission === 'default') {
            await Notification.requestPermission();
        }
    }, []);

    // Toggle notification widget
    const toggleWidget = useCallback(() => {
        const willOpen = !isWidgetOpen;
        setIsWidgetOpen(willOpen);
        if (willOpen) {
            // Fetch latest data immediately when opening
            fetchNotificationData(false);
            setHasNewOrders(false);
        }
    }, [isWidgetOpen, fetchNotificationData]);

    // Close widget
    const closeWidget = useCallback(() => {
        setIsWidgetOpen(false);
    }, []);

    // Toggle sound
    const toggleSound = useCallback(() => {
        setSoundEnabled(prev => !prev);
    }, []);

    // Refresh notifications manually
    const refreshNotifications = useCallback(() => {
        fetchNotificationData(false);
    }, [fetchNotificationData]);

    // Update selected branch and refresh notifications
    const updateSelectedBranch = useCallback((newBranchId, viewAllBranches = false) => {
        const userData = JSON.parse(localStorage.getItem('userData'));
        if (!userData) return;

        const previousBranchId = selectedBranchId;
        setSelectedBranchId(newBranchId);

        // Update socket subscription
        const masterId = userData.role === 1 ? userData.selectedBranch : userData.masterId;
        socketService.changeBranchSubscription(
            previousBranchId,
            newBranchId,
            masterId,
            viewAllBranches || newBranchId === 'all'
        );

        // Fetch updated notifications for new branch
        fetchNotificationData(true, newBranchId);
    }, [selectedBranchId, fetchNotificationData]);

    // Initialize socket connection and fetch initial data
    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('userData'));
        if (!userData) return;

        // Initialize
        getNotificationPreferences();
        requestNotificationPermission();
        fetchNotificationData(false);

        // Connect to socket
        socketService.connect(userData);

        // Listen for order notifications
        socketUnsubscribeRef.current = socketService.addListener('orderNotification', handleSocketNotification);

        // Check socket connection status periodically
        const checkSocketInterval = setInterval(() => {
            const connected = socketService.isSocketConnected();
            setIsSocketConnected(connected);
            
            // If socket disconnected, start fallback polling (less frequent)
            if (!connected && !fallbackPollRef.current) {
                console.log('[NotificationContext] Socket disconnected, starting fallback polling every 2 minutes');
                fallbackPollRef.current = setInterval(() => {
                    console.log('[NotificationContext] Fallback polling - fetching data');
                    fetchNotificationData(true, null, false); // Don't force refresh in fallback
                }, FALLBACK_POLL_INTERVAL);
            }
            
            // If socket reconnected, stop fallback polling and refresh data
            if (connected && fallbackPollRef.current) {
                console.log('[NotificationContext] Socket reconnected, stopping fallback polling');
                clearInterval(fallbackPollRef.current);
                fallbackPollRef.current = null;
                // Force refresh data when socket reconnects
                fetchNotificationData(true, null, true);
            }
        }, 10000); // Check every 10 seconds instead of 5

        // Cleanup
        return () => {
            if (socketUnsubscribeRef.current) {
                socketUnsubscribeRef.current();
            }
            if (fallbackPollRef.current) {
                clearInterval(fallbackPollRef.current);
            }
            clearInterval(checkSocketInterval);
            socketService.disconnect();
        };
    }, [fetchNotificationData, getNotificationPreferences, requestNotificationPermission, handleSocketNotification]);

    // Refresh notification data with force option
    const refreshNotificationData = useCallback((forceRefresh = false) => {
        return fetchNotificationData(false, null, forceRefresh);
    }, [fetchNotificationData]);

    // Context value
    const value = {
        notificationData,
        isLoading,
        isWidgetOpen,
        soundEnabled,
        hasNewOrders,
        isSocketConnected,
        selectedBranchId,
        toggleWidget,
        closeWidget,
        toggleSound,
        markOrdersAsViewed,
        refreshNotifications,
        fetchNotificationData,
        refreshNotificationData,
        updateSelectedBranch
    };

    return (
        <OrderNotificationContext.Provider value={value}>
            {children}
        </OrderNotificationContext.Provider>
    );
};

// Custom hook to use notification context
export const useOrderNotifications = () => {
    const context = useContext(OrderNotificationContext);
    if (!context) {
        throw new Error('useOrderNotifications must be used within an OrderNotificationProvider');
    }
    return context;
};

export default OrderNotificationContext;
